import { faceAxes, faceRects, oppositeFace, type Extents, type FaceWord }
  from './partFaces';
import { axisOf, axisUp, sectionAt, type PartProfile } from './partProfile';
import { categoriesLoaded } from './catalogCategories';
import { inCategoryFamily, type PartItem } from './partsRun';
import type { ByPart } from './partModelSize';

// WHAT A HOLE IS, AS PHASE 4 ANSWERS IT — and why it is not simply what /drill takes.
//
// The drill wants `{face, u, v, diameter, depth, through}`: two millimetre readings from a
// face's bottom-left corner. A model reading a step diagram cannot produce those honestly, and
// this app has already paid for finding that out somewhere else — the movement call used to
// ask for absolute poses and answered them by eye ("I estimate x ≈ −18 to −20cm", from a crop
// at unknown scale), which put a side rail forty millimetres from the frame it is described as
// being held against. Nothing downstream could notice: a wrong distance and a right one are
// both just numbers.
//
// So a hole is said the way a manual says one — FROM AN EDGE, or centred:
//
//   40 mm from the left end, centred across the width
//
// Two answers a reader of a diagram actually holds, and neither of them invented. `centred` is
// exact rather than estimated, and it is true of most furniture holes; a distance from a named
// end is what the drawing dimensions when it dimensions anything at all.
//
// AND IT IS STORED AS GIVEN, resolved late. The extents a part has in Phase 4 are the printed
// ones — a dimension drawing gives two axes of a panel and no thickness — and the extents it
// has once its mesh exists are the mesh's. Resolving at write time would freeze the first set
// into every hole; resolving at read time lets the same answer land correctly on whatever the
// part turns out to measure.
export type HoleAt =
  | { centred: true }
  // `from` is one of the two edges bounding this axis of the face, named by the face it runs
  // toward — the same six words everything else in this app names a direction with.
  | { from: FaceWord; mm: number };

export const isCentred = (at: HoleAt): at is { centred: true } =>
  (at as { centred?: boolean }).centred === true;

// ONE BORE IN ONE SUPPLIED PART, and the gesture that puts something in it.
//
// `part` IS A SUPPLIED PART AND NEVER A PIECE. A gesture names what is on the table — "drive
// {203} home" is one violet object where the manual sees a rail bolted to a frame — and that
// is right for the sentence and useless here: Phase 5 models the rail, so a hole filed under
// the rail-and-frame is a hole nothing can drill. The prompt requires a plank and the parser
// refuses anything else (see pieceContents, which is how the plank is found).
export type StepHole = {
  // H101, H102, H201 — the step that first declared it, then its place in that step. The same
  // shape as a piece's id and for the same reason (see stepPieceBase), with the letter in
  // front so the two ranges cannot be confused: a piece of step 5 is 501 and a hole of step 5
  // is H501.
  id: string;
  // The letter of the sub-step that fills it — where in the step this hole is first known
  // about. A hole is not created by the gesture, strictly: flat-pack parts arrive bored. It is
  // where the manual REVEALS it, which is the only moment anything can be inferred from.
  at: string;
  part: string;
  // WHICH OF A MIRROR PAIR THE MANUAL BORES IT IN, where the part is one — a fact about the
  // diagram, and about which unit a reader is being told to pick up. Absent on everything
  // else, which is nearly every hole, and absent too where the drawing does not say which of
  // the two it is showing.
  //
  // NOT A COORDINATE FRAME, and that is exactly what it used to be. `face`, `u` and `v` are
  // ALWAYS THE LEFT UNIT'S. The two units are mirror images, so one set of holes describes
  // both of them, and the mesh IS the left unit — the right one is the same file drawn
  // reflected (see PartViewer, and the drill, which refuses the right hand for this reason).
  // A bore read off a drawing of the right unit is mirrored into the left unit's frame before
  // it is written: swap the words 'left' and 'right' wherever they appear — in the face name
  // and in the edge a distance is measured from — and change nothing else.
  //
  // THE SWAP IS MADE FOR THE MODEL, NOT BY IT (see _mirrored, where the reply lands). It was a
  // paragraph of prompt asking for a reflection to be performed in the head, beside the turn
  // from page to product that is the thing this call actually gets wrong; what reaches the
  // parser is the unit the drawing shows, named in this field, and two words change.
  //
  // WHAT THE OLD MEANING COST: holesOfPart filtered on this field, so a hole the manual showed
  // on the right rail matched neither hand the viewer could drill from — the left hand skipped
  // it as the other one's, and the right hand refuses to drill at all. It was never cut, and
  // the pair came out with half its holes and no complaint.
  //
  // SET ON EVERY HOLE A LINE MAKES IN A PAIR since a pair's line became one per unit (see
  // holesOfLine): the hole is in that unit, and only that one. Absent, on an answer from before,
  // it is in both — which is how such a hole was drawn.
  side?: 'left' | 'right';
  face: FaceWord;
  u: HoleAt;
  v: HoleAt;
  // FROM THE FASTENER, MOSTLY. A M6 x 70 screw states its own diameter and a dowel is its own
  // diameter; null where the hardware carries no designation and the drawing prints nothing,
  // which is a hole whose place is known and whose bore is not — a different thing from a hole
  // nobody found, and worth being able to tell apart.
  // WHAT THE MODEL SAID IT WAS, and no longer what anything bores. The width is resolved off
  // the fastener now — see holeWidthOf — because a per-hole answer from a name is a four-way
  // choice made afresh on every row, and one manual came back with three holes taking one
  // barrel nut answered 10, null and null. Kept because it is evidence: it is what was
  // ANSWERED, it is what the prior-holes block reports back to the next step, and a field
  // deleted is a disagreement nobody can find afterwards.
  diameterMm: number | null;
  depthMm: number | null;
  through: boolean;
  // HOW FAR BELOW THE FACE THE HEAD SITS, where it is sunk — a screw in a counterbore or a pocket:
  // the hole's first `sunkMm` are empty, as wide as the head, and the screw starts there. On the
  // entry hole of a line only; absent is flush.
  sunkMm?: number;
  // HOW FAR UNDER ITS BOX FACE THE WOOD BEGINS AT ITS MOUTH, measured by the line that made it on
  // the part's mesh as it was BEFORE drilling (see holesOfLine). The joint view draws the hardware
  // from there; measured again on the drilled mesh, a ray down the bore met no wood and the screw
  // stood on the box face. Absent: not measured (an answer from before it was kept), and said.
  surfaceMm?: number;
  // AND WHERE A THROUGH HOLE LEAVES THE WOOD: how much of it the line crosses from its mouth, on the
  // same mesh. A curved rail's box runs past its wood, and a bore drawn to the box's far face stood
  // out of the part into the air beyond it. Through holes only; absent, the mark goes to the box's.
  woodMm?: number;
  // ON A LINE THAT DOES NOT WORK — air between the parts it joins, too short to reach, nothing to
  // bite into (see holesOfLine.fits): kept, said by the line's notes, and drilled nowhere.
  unfit?: true;
  // WHICH PIECE OF HARDWARE GOES IN IT. The whole use of the answer downstream: a hole with a
  // JCBC screw in it has that screw's diameter, that screw's depth, and a thread if it is
  // driven rather than pushed. Null only where the gesture names no hardware at all.
  via: string | null;
  // WHICH LINE MADE IT: the id of the hole that line's hardware enters by (see lineHoles). The
  // one sure way to gather a line's holes — they need not share a unit: a dowel from a rail
  // into the frame has a hole in the left rail and one in a frame that has no hand. Absent on
  // answers from before it was kept.
  line?: string;
  // ITS PLACE IS OFF THE PART'S WOOD, as its line measured it (see holesOfLine) — said under the
  // joint's table, and so never sent to the drill, which would only refuse it a second time.
  offWood?: true;
};

// WHAT ONE STEP FOUND: the holes it declares, and the holes its gestures fill that some
// earlier gesture already declared.
//
// TWO LISTS, BECAUSE A HOLE IS DECLARED ONCE AND FILLED MORE THAN ONCE. A kind 3 puts a screw
// into a rail and a kind 4 tightens that same screw a gesture later: one bore, two gestures.
// Written as one list it would say the rail has two holes. This is the same idiom the pieces
// use — declared where it is made, referenced by id afterwards — and the model is already
// taught that pattern by the call beside this one.
export type StepHoles = {
  holes: StepHole[];
  // sub-step letter -> ids of holes declared elsewhere that this gesture fills.
  fills: Record<string, string[]>;
};

// THE FIRST ID THIS STEP'S HOLES TAKE. A function of the step alone, exactly as a piece's is:
// a step can be told its numbering without waiting for the steps before it to be read, and an
// id read off a chip says which step found it before it says anything else.
export const holeIdBase = (stepNo: number) => `H${stepNo * 100 + 1}`;

// A HOLE ID'S NUMBER: 101 for "H101", "H101-A" and "H101-X" — the holes of one line share a number,
// lettered in the order the hardware goes through them, and the cross hole across it is X (see
// hardwareInteractionsPrompt). NaN for anything else.
export const holeNumber = (id: string) => parseInt(id.replace(/^H/i, ''), 10);


// WHERE A HOLE ACTUALLY IS ON ITS FACE, in millimetres from the face's bottom-left corner as
// /drill and every other reader of a place in this app measures it.
//
// This is the resolution the answer was deliberately not written in. `faceRects` says how big
// each face is and which way u and v run across it; an edge-relative answer is subtracted from
// that where it is measured from the far end, and a centred one is half of it.
//
// Null where the part has no three measured axes: a face with no size is a face a point cannot
// be placed on, and this app says so by absence everywhere else it comes up.
export function holePlace(
  hole: StepHole,
  dims: Extents,
  // WHAT SHAPE THE PART IS INSIDE ITS BOX, when anybody has measured it — see partProfile.
  // Given, "centred" on the SECOND of the two lines stops meaning half the box and starts
  // meaning halfway between the two surfaces at the place the first line resolved to.
  //
  // OPTIONAL, AND THE BOX IS THE FALLBACK. A part nobody has opened in the viewer has no
  // profile, and neither has a record written before this existed. Both answer what they
  // always answered, which is also the right answer for a plank.
  profile?: PartProfile | null,
): { u: number; v: number } | null {
  const rects = faceRects(dims);
  const rect = rects?.find((r) => r.face === hole.face);
  if (!rect) return null;
  // `uTo` is the face u runs TOWARD, so u is 0 at its opposite. A distance measured from the
  // far edge is the span less that distance — which is the arithmetic the model would
  // otherwise have had to do, on a number it would have had to be told.
  //
  // AN EDGE THAT DOES NOT BOUND THIS AXIS PLACES NOTHING, and is refused rather than read as
  // the nearest thing: "40 mm from the top" measured across a top face is a reading of no
  // distance at all, and quietly taking it as 40 from the u-origin would put a bore somewhere
  // nobody named — which is the failure this whole form was chosen to avoid. The obvious
  // repair, where the model has named a real edge under the wrong key, is done once when the
  // answer lands (see stepHolesRun); what reaches here and is still wrong is unplaceable.
  const solve = (at: HoleAt, span: number, to: FaceWord): number | null => {
    if (isCentred(at)) return span / 2;
    if (at.from !== to && at.from !== oppositeFace(to)) return null;
    const mm = Math.max(0, Math.min(span, at.mm));
    return at.from === to ? span - mm : mm;
  };
  const u = solve(hole.u, rect.u, rect.uTo);
  if (u == null) return null;
  // THE SECOND LINE IS ANSWERED AT THE FIRST LINE'S ANSWER, and only the second.
  //
  // The order is the one the table prints, which is the face's own (u, v). It has to be AN
  // order and it cannot be both: "centred, centred" against the material would need each
  // coordinate to be known before the other. So the first is resolved against the box and the
  // second against the section there — which loses nothing, because a section taken over the
  // whole face is the box: the extent of the material along an axis IS the bounding box along
  // that axis. Only the second line has a narrower question to ask.
  //
  // THE ENVELOPE, WITHOUT A FALLBACK. Half of the first material to the last, which on a part
  // with a gap in that section is a point in the gap. That is the answer: the alternative
  // walks (see partProfile).
  const v = isCentred(hole.v)
    ? centredOn(rect, profile, u) ?? solve(hole.v, rect.v, rect.vTo)
    : solve(hole.v, rect.v, rect.vTo);
  return v == null ? null : { u, v };
}

// HALFWAY THROUGH THE WOOD ALONG `v`, at the u already resolved — in the face's own
// millimetres, or null wherever the profile cannot answer and the box has to.
//
// The two conversions are the whole of it. u is measured from the edge OPPOSITE the one it
// runs toward, and the profile is measured from the box's minimum on that world axis, so a
// face whose u runs down its axis has its fraction turned over. The same in reverse for the
// answer, which comes back as a fraction along the world axis and has to be said as a
// distance up the face.
function centredOn(
  rect: { u: number; v: number; uTo: FaceWord; vTo: FaceWord },
  profile: PartProfile | null | undefined,
  u: number,
): number | null {
  if (!profile || !rect.u || !rect.v) return null;
  const along = u / rect.u;
  const band = sectionAt(
    profile,
    axisOf(rect.uTo),
    axisOf(rect.vTo),
    axisUp(rect.uTo) ? along : 1 - along,
  );
  if (!band) return null;
  const mid = (band[0] + band[1]) / 2;
  return (axisUp(rect.vTo) ? mid : 1 - mid) * rect.v;
}

// WHICH EDGE EACH OF A FACE'S TWO MEASUREMENTS IS COUNTED FROM — [u's origin, v's origin].
//
// What makes a resolved number checkable. "358" is a distance from a corner nothing on screen
// names, and which corner it is turns on the face: the right face counts its depth from the
// front and the left face counts it from the back. So the number is drawn with this beside it.
export const holeOrigins = (face: FaceWord): [FaceWord, FaceWord] => {
  const { uTo, vTo } = faceAxes(face);
  return [oppositeFace(uTo), oppositeFace(vTo)];
};

// WHICH TWO EDGES BOUND EACH AXIS OF A FACE — what the prompt offers and what the parser
// checks an answer against. A `from` outside the pair describes no distance across that face:
// "40 mm from the top" on a top face is a reading of nothing.
export function holeEdges(face: FaceWord, dims: Extents):
  { u: FaceWord[]; v: FaceWord[] } | null {
  const rect = faceRects(dims)?.find((r) => r.face === face);
  return rect
    ? { u: [oppositeFace(rect.uTo), rect.uTo], v: [oppositeFace(rect.vTo), rect.vTo] }
    : null;
}

// WHAT SHAPE OF HOLE, WORKED OUT FROM WHAT GOES IN IT.
//
// NOT ASKED OF A MODEL, and that is the point of it being here. Phase 4 already names the
// fastener each bore is for, and a fastener's hole is decided by the fastener: a dowel needs a
// floor to bottom out on, a countersunk head needs a cone to sit in, a bolt running to a nut
// needs neither. Asking a model to answer it a second time would be asking it to re-derive
// something already in front of it, and give a different answer some of the time.
//
// THE THREE ARE THE THREE CUTS THIS MANUAL ACTUALLY CONTAINS — see _HOLE_STYLES in
// mesh_repair_blender.py, which makes them. Not a taxonomy of drilling: a hole here is
// straight, flat-floored, or funnelled at the mouth, and a value that made no difference to
// the mesh would be a column of words nobody could check against the picture.
export type HoleStyle = 'plain' | 'flat' | 'countersunk';

// WHOSE HOLE HAS A FLOOR: the fasteners that SEAT in one. A dowel and a barrel nut are both
// cylinders that stop where the hole stops, and a cone under either of them is a part standing
// a couple of millimetres proud of where the drawing puts it.
const SEATED = new Set(['dowels', 'barrel nuts']);
const SCREWS = new Set(['screws']);

// WHICH HARDWARE MAKES A HOLE AT ALL — the three families whose parts go INTO wood.
//
// The other four do not, and each for its own reason. A WASHER goes around a screw. A
// FASTENER COVER is fitted last and prised out first: a wood button plugs the counterbore of
// a screw that is already driven, so the hole it sits in is that screw's and not a second one.
// PROTECTIVE FEET stay on the outside of the assembly, against the floor. And OTHER is the
// catch-all — a cam lock in it does bore, a cable tie does not, and nothing in this app can
// size either, so a hole answered against one comes out widthless and undrillable anyway.
//
// A WHITELIST AND NOT A LIST OF EXCEPTIONS, so a category somebody adds tomorrow bores nothing
// until it is said to. The failure then is a hole nobody found, which shows up as a missing
// row that a person reading the manual will notice; the other way round is a bore cut into a
// mesh because a category was invented, which nothing checks.
//
// WHY IT IS WORTH SAYING AT ALL: a step that drives a screw and caps it with a button names
// two pieces of hardware and holds one bore. Left to work that out from the names, the
// analysis reasoned its way there out loud — "the Wood buttons are not hardware! they FILL the
// screw head" — which is a model rescuing itself from a list that should not have offered the
// button in the first place.
const BORES = new Set([...SCREWS, ...SEATED]);

// Whether a part is one of them. False for a component — a plank does not go into another
// plank — and false before the ontology has landed, which is the same guard every family test
// in this file carries.
export const boresHole = (part: PartItem | undefined | null): boolean =>
  !!part && categoriesLoaded() && inCategoryFamily(part.category, BORES);

// A POINTED SCREW, FOR WHICH NO PART IS CUT (Louis 2026-10-29): a wood or chipboard screw makes its own
// way through the part it goes through and into the one it bites. Its line is still found — where it
// goes in is where the assembly drives it, into solid wood — but nothing is drilled for it: the drill
// skips it, and the tables say so (see PartSize.tip).
// AND ITS HOLES SHOWN AS P, FOR POSITION (Louis 2026-10-29): "P301-A" where the screw is pointed, so
// a place nothing is drilled at does not read as a hole. Shown only: the stored id stays "H301-A",
// which is what the hardware table, Find holes and Phase 6 find each other by.
export const shownHoleId = (id: string, pointed: boolean) => (pointed ? id.replace(/^H/, 'P') : id);

export const makesOwnHole = (part: PartItem | undefined | null): boolean =>
  !!part && categoriesLoaded() && inCategoryFamily(part.category, SCREWS) && part.size?.tip === 'pointed';

// HOW MUCH WOOD A BORE NEEDS ROUND IT: thinner than this and the wall splits, in the drilling or
// under the hardware's load — a hole "inside the part" by half a millimetre is not one. Asked of
// every hole the joint view walks (see holeAir), and so of every place Find holes is offered.
export const WALL_MM = 3;
// AND BETWEEN TWO OF THEM: bores side by side, the widest of them `widths` across, centre to centre —
// WALL_MM of wood left between their sides. Null where no width is known.
export const holeSpacingMm = (widths: (number | null)[]): number | null => {
  const known = widths.filter((w): w is number => w != null);
  return known.length ? Math.max(...known) + WALL_MM : null;
};

// HOW WIDE A BORE IS: THE INSERTION TABLE'S HOLE DIAMETER FOR ITS FASTENER (see insertionRun).
//
// It used to be the model's answer, one hole at a time, and it did not come back the same way
// twice: one manual had three holes taking one barrel nut answered 10, null and null. Then it
// was the parts list's printed figures — a screw's thread, a dowel's body diameter — each kind
// keeping its number in a different field.
//
// NOW ONE NUMBER PER FASTENER, answered once for every hole it goes into — a screw's shank, a
// dowel's or a barrel nut's body.
//
// AND NO FALLBACK. Not to the printed figure, not to the box: a fastener the insertion table has
// not answered gives a hole with no width, which is not drillable and says so.
//
// Only hardware that makes a hole (see BORES): a washer goes round a screw, and a cover plugs the
// counterbore of a screw already driven, which is that screw's hole and not a second one.
//
// WHAT IS NOT MODELLED: a screw's hole is one bore here, at its shank, where a workshop would
// drill a clearance hole in the near panel and a narrower pilot in the far one. Which side of a
// joint a part is on is a fact about the JOINT and a hole names one part — see the gesture, which
// knows both parts. The visible half of that is carried by holeStyleOf, which funnels the mouth
// of a through screw hole and leaves a blind one plain.
export function holeWidthOf(
  hole: StepHole,
  items: PartItem[],
  // WHAT EACH PART'S 3D MODEL MEASURES, by part id — see partModelSize. Optional because a
  // caller may have none in hand; absent, no width.
  models?: ByPart,
): number | null {
  // The same guard holeStyleOf carries, for the same reason: the family tests read an ontology
  // that arrives from the server, and before it lands every one of them answers false.
  if (!categoriesLoaded()) return null;
  const via = hole.via == null ? null : items[Number(hole.via)];
  if (!via || !inCategoryFamily(via.category, BORES)) return null;
  return models?.[String(hole.via)]?.insertion?.holeDiameterMm ?? null;
}

// AND A SCREW'S HOLE DEPENDS ON WHICH SIDE OF THE JOINT IT IS, which is the one thing about
// this that is not simply a property of the fastener. One chipboard screw makes two different
// holes: a countersunk clearance hole in the panel it passes THROUGH, where its head lands and
// has to finish flush, and a plain bore in whatever it runs into. So a screw hole that goes
// through is the head's side by construction, and a blind one is not.
//
// It is a rule about this manual's hardware and not a law: a bolt whose head is meant to stand
// proud of a through hole would come out countersunk here. That is a wrong answer with a right
// shape — the mouth opens a couple of millimetres — and it is one line to change when a manual
// turns up that needs it.
// NULL WHERE IT CANNOT BE KNOWN YET, and never a guess dressed as an answer. The rule reads
// the category ontology, which arrives from the server: before it lands, every family test
// answers false and every hole in the manual would come out `plain` — the right shape for some
// of them, silently the wrong one for the rest, with nothing on screen to say which. The
// caller waits instead; it is one fetch and it is already on its way.
export function holeStyleOf(hole: StepHole, items: PartItem[]): HoleStyle | null {
  if (!categoriesLoaded()) return null;
  // BY FAMILY AND NOT BY THE CATEGORY'S OWN NAME. The ontology is somebody's to rearrange —
  // "Dowels" may grow children — and a rule matching the leaf name alone would quietly stop
  // firing the day it does, leaving every dowel hole pointed with nothing to say why.
  const via = hole.via == null ? null : items[Number(hole.via)];
  if (!via) return 'plain';
  if (inCategoryFamily(via.category, SEATED)) return 'flat';
  // NOT WHERE THE HEAD IS SUNK: it sits on the counterbore's floor, below the surface, and a cone
  // at the mouth only widened it on a slope (see StepHole.sunkMm).
  if (inCategoryFamily(via.category, SCREWS) && hole.through) return hole.sunkMm ? 'plain' : 'countersunk';
  return 'plain';
}

// WHICH OF THE TWO WAYS A FASTENER RUNS ALONG ITS LINE — see holeLines. A screw goes in from its
// head and runs forward only; a dowel or a barrel nut sits in a hole and stops where it stops.
// Null for hardware that bores nothing, and before the ontology has landed.
export function fastenerKind(part: PartItem | undefined | null): 'screw' | 'seated' | null {
  if (!part || !categoriesLoaded()) return null;
  if (inCategoryFamily(part.category, SCREWS)) return 'screw';
  if (inCategoryFamily(part.category, SEATED)) return 'seated';
  return null;
}

// HOW FAR A PIECE OF HARDWARE GOES INTO THE WOOD, along the axis it goes in by: how much of it
// the insertion table says is inside the parts (see insertionRun). NO FALLBACK to the printed
// length: null until it has answered — a line that cannot say how far it reaches, and says so.
export function hardwareReachOf(id: string, models?: ByPart): number | null {
  return models?.[id]?.insertion?.insideMm || null;
}

// WHETHER TWO HANDS CAN BE ONE UNIT: equal, or either unsaid (a hole on no hand of a pair is on
// both, and a unit of no hand is the only one of its part).
export const sameUnit = (a: string | null | undefined, b: string | null | undefined) =>
  !a || !b || a === b;

// WHETHER A HOLE IS ON ONE UNIT OF A PART: that part, and that hand or no hand said.
export const holeOnUnit = (h: StepHole, part: string, hand: string | null | undefined) =>
  h.part === part && sameUnit(h.side, hand);
